import get from 'lodash/get';
import {
  fieldStarterSeat,
  fieldOfficeSeats,
  fieldTypeOfInstallation,
  limitedExtensionOdysseyAIOdysseyAI
} from '../../utils/constant';
import { getFieldValue } from './formCommon';
import { checkHwcAllowance, supportsHardwareCredit } from './hwcEligibility';

/** Add-on installation values (EN + FR contentful) */
const ADD_ON_TYPE_VALUES = ['Add On', 'Ajouter'];

const matchesYes = v => v === 'Yes' || v === 'Oui';

/**
 * Device filtering utility for BCON modal forms
 * Filters devices based on orderData and form field values
 */

/**
 * Check if a device answer value matches the expected value
 * @param {string} deviceAnswer - Device answer from API (e.g., "Yes, Oui")
 * @param {string} expectedValue - Expected value to match
 * @returns {boolean} - True if match found
 */
const doesAnswerMatch = (deviceAnswer, expectedValue) => {
  if (!deviceAnswer || !expectedValue) return false;
  
  // Split comma-separated values and trim whitespace
  const answerValues = deviceAnswer.split(',').map(val => val.trim());
  
  // Check if any of the answer values match the expected value
  return answerValues.some(answer => 
    answer.toLowerCase() === expectedValue.toLowerCase()
  );
};

/**
 * Get field value with fallback to 'No' for undefined/null Yes/No questions
 * @param {string} fieldName - Field name to get value for
 * @param {object} formFields - Form fields object
 * @param {boolean} isYesNoQuestion - Whether this is a Yes/No question
 * @returns {string} - Field value or 'No' for undefined Yes/No questions
 */
const getFieldValueWithFallback = (fieldName, formFields, isYesNoQuestion = false) => {
  const value = getFieldValue(fieldName, formFields);
  
  // For Yes/No questions, treat undefined/null as 'No'
  if (isYesNoQuestion && (value === undefined || value === null || value === '')) {
    return 'No';
  }
  
  return value;
};

/**
 * Check if device supports spare devices
 * @param {object} device - Device object from API
 * @returns {boolean} - True if device supports spare devices
 */
const supportsSpareDevices = (device) => {
  const spareDeviceAnswer = device.answers && device.answers.doesCustomerRequireSpareDevices;
  if (!spareDeviceAnswer) return false;
  
  // Convert to string and normalize
  const answerString = String(spareDeviceAnswer).trim();
  if (!answerString) return false;
  
  // Split comma-separated values and trim whitespace, then convert to lowercase
  const answerValues = answerString.split(',').map(val => val.trim().toLowerCase()).filter(val => val.length > 0);
  
  // Check if device has "Yes" or "Oui" - if so, it supports spare devices
  const hasYes = answerValues.some(val => val === 'yes' || val === 'oui');
  
  // Check if device has "No" or "Non"
  const hasNo = answerValues.some(val => val === 'no' || val === 'non');
  
  // STRICT LOGIC: Only include devices that have "Yes" or "Oui"
  // Exclude devices that ONLY have "No" or "Non" (no "Yes" or "Oui")
  if (hasYes) {
    // Device has "Yes" or "Oui" - it supports spare devices (even if it also has "No" or "Non")
    return true;
  }
  
  // Device does NOT have "Yes" or "Oui"
  // If it has "No" or "Non", it does NOT support spare devices - EXCLUDE IT
  if (hasNo) {
    return false;
  }
  
  // If we can't determine (unexpected value), exclude for safety
  return false;
};

/**
 * Calculate maximum devices allowed based on seat count (orderData + formFields).
 * Odyssey AI cap uses orderData.isThisOdysseyAI and limitedExtensionOdysseyAIOdysseyAI; other numeric fields prefer form then order.
 * @param {object} formFields - Form fields object
 * @param {object} [orderData] - Order payload (BCON)
 * @returns {number} - Maximum devices allowed
 */
export const calculateMaxDevices = (formFields, orderData = {}) => {
  const starterSeats = Number(
    getFieldValue(fieldStarterSeat, formFields) ||
      get(orderData, fieldStarterSeat, 0) ||
      0
  );
  const officeSeats = Number(
    getFieldValue(fieldOfficeSeats, formFields) ||
      get(orderData, fieldOfficeSeats, 0) ||
      0
  );
  const totalSeats = starterSeats + officeSeats;

  const isOdysseyAiOrder = matchesYes(get(orderData, 'isThisOdysseyAI', ''));

  let limitedExtQty = 0;
  if (isOdysseyAiOrder) {
    const limitedExtRaw =
      getFieldValue(limitedExtensionOdysseyAIOdysseyAI, formFields) ||
      get(orderData, limitedExtensionOdysseyAIOdysseyAI, '');
    limitedExtQty = Number(limitedExtRaw) || 0;
  }

  if (isOdysseyAiOrder && limitedExtQty > 0) {
    const typeOfInstallation =
      getFieldValue(fieldTypeOfInstallation, formFields) ||
      get(orderData, fieldTypeOfInstallation, '');
    const isAddon = ADD_ON_TYPE_VALUES.includes(typeOfInstallation);

    if (totalSeats === 0 && isAddon) {
      return limitedExtQty;
    }
    return totalSeats + limitedExtQty;
  }

  return totalSeats;
};

/**
 * Calculate current device count (sum of quantities)
 * @param {object} formFields - Form fields object
 * @returns {number} - Current device count (sum of quantities)
 */
export const calculateCurrentDevices = (formFields) => {
  return (formFields.devices || []).reduce((acc, item) => acc + (item.quantity || 0), 0);
};

/**
 * Calculate current total device count (devices + spare devices, sum of quantities)
 * @param {object} formFields - Form fields object
 * @returns {number} - Current total device count (sum of quantities)
 */
export const calculateCurrentTotalDevices = (formFields) => {
  const currentDevices = (formFields.devices || []).reduce((acc, item) => acc + (item.quantity || 0), 0);
  const currentSpareDevices = (formFields.additionalSpareDevices || []).reduce((acc, item) => acc + (item.quantity || 0), 0);
  return currentDevices + currentSpareDevices;
};

/**
 * Filter devices for addDevices modal
 * @param {array} devices - Array of devices from API
 * @param {object} orderData - Order data object
 * @param {object} formFields - Form fields object
 * @param {object} [contentfulValues] - { valuePaymentUpfront }; enables the HWC allowance filter
 * @returns {array} - Filtered devices array
 */
export const filterDevicesForAddDevices = (devices, orderData, formFields, contentfulValues) => {
  if (!devices || !Array.isArray(devices)) return [];

  // Check seat-based validation
  const maxDevices = calculateMaxDevices(formFields, orderData);
  const currentDevices = calculateCurrentDevices(formFields);

  // If already at device limit, return empty array
  if (currentDevices >= maxDevices) {
    return [];
  }

  // Once the HWC allowance is used up, only non-HWC models remain addable
  const hwcAllowance = contentfulValues
    ? checkHwcAllowance(formFields, orderData, contentfulValues)
    : null;
  const excludeHwcDevices = Boolean(
    hwcAllowance && hwcAllowance.remaining === 0
  );

  return devices.filter(device => {
    if (excludeHwcDevices && supportsHardwareCredit(device)) {
      return false;
    }

    const answers = device.answers || {};
    
    // Check coperToFibreMigration from orderData
    if (orderData.coperToFibreMigration !== undefined && orderData.coperToFibreMigration !== null) {
      // If C2F value exists in orderData, filter by that value
      if (!doesAnswerMatch(answers.coperToFibreMigration, orderData.coperToFibreMigration)) {
        return false;
      }
    } else {
      // If C2F key is not present in orderData, only show devices with "No" or "Non" answer
      if (!doesAnswerMatch(answers.coperToFibreMigration, 'No') && 
          !doesAnswerMatch(answers.coperToFibreMigration, 'Non')) {
        return false;
      }
    }
    
    // Check customerSegment from orderData
    if (orderData.customerSegment) {
      if (!doesAnswerMatch(answers.customerSegment, orderData.customerSegment)) {
        return false;
      }
    }
    
    // Check customerRequireAIFeatures (Version) - only filter if orderData has a value and device has an answer
    if (orderData.customerRequireAIFeatures) {
      const deviceVersionAnswer = answers.customerRequireAIFeatures;
      if (deviceVersionAnswer) {
        if (!doesAnswerMatch(deviceVersionAnswer, orderData.customerRequireAIFeatures)) {
          return false;
        }
      }
    }
    
    // Check contractTerm from formFields
    const contractTerm = getFieldValue('contractTerm', formFields);
    if (contractTerm) {
      if (!doesAnswerMatch(answers.contractTerm, contractTerm)) {
        return false;
      }
    }
    
    // Check typeOfInstallation from formFields
    const typeOfInstallation = getFieldValue('typeOfInstallation', formFields);
    if (typeOfInstallation) {
      if (!doesAnswerMatch(answers.typeOfInstallation, typeOfInstallation)) {
        return false;
      }
    }
    
    // Check businessConnectEdition from formFields
    const businessConnectEdition = getFieldValue('businessConnectEdition', formFields);
    if (businessConnectEdition) {
      if (!doesAnswerMatch(answers.businessConnectEdition, businessConnectEdition)) {
        return false;
      }
    }
    
    // Check otherTelusServices from formFields (Yes/No question - treat undefined as 'No')
    const otherTelusServices = getFieldValueWithFallback('otherTelusServices', formFields, true);
    if (otherTelusServices) {
      if (!doesAnswerMatch(answers.otherTelusServices, otherTelusServices)) {
        return false;
      }
    }
    
    return true;
  });
};

/**
 * Filter devices based on orderData and formFields (without seat limit check)
 * @param {array} devices - Array of devices from API
 * @param {object} orderData - Order data object
 * @param {object} formFields - Form fields object
 * @returns {array} - Filtered devices array
 */
const filterDevicesByConfiguration = (devices, orderData, formFields) => {
  return devices.filter(device => {
    const answers = device.answers || {};
    
    // Check coperToFibreMigration from orderData
    if (orderData.coperToFibreMigration !== undefined && orderData.coperToFibreMigration !== null) {
      // If C2F value exists in orderData, filter by that value
      if (!doesAnswerMatch(answers.coperToFibreMigration, orderData.coperToFibreMigration)) {
        return false;
      }
    } else {
      // If C2F key is not present in orderData, only show devices with "No" or "Non" answer
      if (!doesAnswerMatch(answers.coperToFibreMigration, 'No') && 
          !doesAnswerMatch(answers.coperToFibreMigration, 'Non')) {
        return false;
      }
    }
    
    // Check customerSegment from orderData - but only if C2F is NOT "Yes" or "Oui"
    const isC2FYes = orderData.coperToFibreMigration && 
      (orderData.coperToFibreMigration === 'Yes' || orderData.coperToFibreMigration === 'Oui');
    
    if (!isC2FYes && orderData.customerSegment) {
      if (!doesAnswerMatch(answers.customerSegment, orderData.customerSegment)) {
        return false;
      }
    }

    // Check customerRequireAIFeatures (Version) - only filter if orderData has a value and device has an answer
    if (orderData.customerRequireAIFeatures) {
      const deviceVersionAnswer = answers.customerRequireAIFeatures;
      if (deviceVersionAnswer) {
        if (!doesAnswerMatch(deviceVersionAnswer, orderData.customerRequireAIFeatures)) {
          return false;
        }
      }
    }
    
    // Check contractTerm from formFields
    const contractTerm = getFieldValue('contractTerm', formFields);
    if (contractTerm) {
      if (!doesAnswerMatch(answers.contractTerm, contractTerm)) {
        return false;
      }
    }
    
    // Check typeOfInstallation from formFields
    const typeOfInstallation = getFieldValue('typeOfInstallation', formFields);
    if (typeOfInstallation) {
      if (!doesAnswerMatch(answers.typeOfInstallation, typeOfInstallation)) {
        return false;
      }
    }
    
    // Check businessConnectEdition from formFields
    const businessConnectEdition = getFieldValue('businessConnectEdition', formFields);
    if (businessConnectEdition) {
      if (!doesAnswerMatch(answers.businessConnectEdition, businessConnectEdition)) {
        return false;
      }
    }
    
    // Check otherTelusServices from formFields (Yes/No question - treat undefined as 'No')
    const otherTelusServices = getFieldValueWithFallback('otherTelusServices', formFields, true);
    if (otherTelusServices) {
      if (!doesAnswerMatch(answers.otherTelusServices, otherTelusServices)) {
        return false;
      }
    }
    
    return true;
  });
};

/**
 * Filter devices for addSpareDevices modal
 * @param {array} devices - Array of devices from API
 * @param {object} orderData - Order data object
 * @param {object} formFields - Form fields object
 * @returns {array} - Filtered devices array
 */
export const filterDevicesForAddSpareDevices = (devices, orderData, formFields) => {
  if (!devices || !Array.isArray(devices)) return [];

  // Check seat-based validation for spare devices
  const maxDevices = calculateMaxDevices(formFields, orderData);
  const maxTotalDevices = maxDevices * 4; // Spare devices can be up to 4x the seat count
  const currentTotalDevices = calculateCurrentTotalDevices(formFields);

  // If already at total device limit, return empty array
  if (currentTotalDevices >= maxTotalDevices) {
    return [];
  }

  // Filter devices by configuration (without primary device limit check)
  const filteredDevices = filterDevicesByConfiguration(devices, orderData, formFields);
  
  // Then filter for spare device support
  const spareDevices = filteredDevices.filter(device => {
    const supports = supportsSpareDevices(device);
    return supports;
  });
  
  return spareDevices;
};

/**
 * Check if device limit is reached for primary devices
 * @param {object} formFields - Form fields object
 * @returns {object} - Object with isLimitReached boolean and message
 */
export const checkPrimaryDeviceLimit = (formFields, orderData = {}) => {
  const maxDevices = calculateMaxDevices(formFields, orderData);
  const currentDevices = calculateCurrentDevices(formFields);
  const isLimitReached = currentDevices >= maxDevices;
  
  return {
    isLimitReached,
    currentDevices,
    maxDevices,
    message: isLimitReached 
      ? `Device limit reached (${currentDevices}/${maxDevices} devices)`
      : `Can add ${maxDevices - currentDevices} more devices (${currentDevices}/${maxDevices})`
  };
};

/**
 * Check if spare devices can be added (checks both limit and availability)
 * @param {object} formFields - Form fields object
 * @param {object} orderData - Order data object
 * @returns {Promise<object>} - Object with canAdd boolean and message
 */
export const checkSpareDeviceAvailability = async (formFields, orderData) => {
  const maxDevices = calculateMaxDevices(formFields, orderData);
  const maxTotalDevices = maxDevices * 4;
  const currentTotalDevices = calculateCurrentTotalDevices(formFields);
  const isTotalLimitReached = currentTotalDevices >= maxTotalDevices;
  
  if (isTotalLimitReached) {
    return {
      canAdd: false,
      isLimitReached: true,
      currentTotalDevices,
      maxTotalDevices,
      maxDevices,
      message: `Total device limit reached (${currentTotalDevices}/${maxTotalDevices} total devices)`
    };
  }
  
  // Check if there are any devices available that support spare devices
  try {
    const request = (await import('../../utils/request')).default;
    const response = await request.get('/orderDevices?limit=1000');
    const devices = (response.data && response.data.data) || [];

    // Filter devices for spare devices
    const spareDevices = filterDevicesForAddSpareDevices(devices, orderData, formFields);
    const hasAvailableDevices = spareDevices.length > 0;
    
    // Additional debugging: check how many devices support spare devices in general
    const devicesSupportingSpareDevices = devices.filter(device => {
      const supports = supportsSpareDevices(device);
      return supports;
    });

    return {
      canAdd: hasAvailableDevices,
      isLimitReached: false,
      currentTotalDevices,
      maxTotalDevices,
      maxDevices,
      availableDevicesCount: spareDevices.length,
      totalDevicesSupportingSpare: devicesSupportingSpareDevices.length,
      message: (() => {
        if (hasAvailableDevices) {
          return `Can add ${maxTotalDevices - currentTotalDevices} more total devices (${currentTotalDevices}/${maxTotalDevices})`;
        }
        if (devicesSupportingSpareDevices.length > 0) {
          return `No spare devices match your configuration (${currentTotalDevices}/${maxTotalDevices} total devices)`;
        }
        return `No devices support spare devices (${currentTotalDevices}/${maxTotalDevices} total devices)`;
      })()
    };
  } catch (error) {
    return {
      canAdd: false,
      isLimitReached: false,
      currentTotalDevices,
      maxTotalDevices,
      maxDevices,
      message: `Error checking device availability (${currentTotalDevices}/${maxTotalDevices} total devices)`
    };
  }
};

/**
 * Check if device limit is reached for spare devices (legacy function for backward compatibility)
 * @param {object} formFields - Form fields object
 * @returns {object} - Object with isLimitReached boolean and message
 */
export const checkSpareDeviceLimit = (formFields, orderData = {}) => {
  const maxDevices = calculateMaxDevices(formFields, orderData);
  const maxTotalDevices = maxDevices * 4;
  const currentTotalDevices = calculateCurrentTotalDevices(formFields);
  const isLimitReached = currentTotalDevices >= maxTotalDevices;
  
  return {
    isLimitReached,
    currentTotalDevices,
    maxTotalDevices,
    maxDevices,
    message: isLimitReached 
      ? `Total device limit reached (${currentTotalDevices}/${maxTotalDevices} total devices)`
      : `Can add ${maxTotalDevices - currentTotalDevices} more total devices (${currentTotalDevices}/${maxTotalDevices})`
  };
};

/**
 * Convert device to modal option format
 * @param {object} device - Device object from API
 * @param {boolean} isSpareDevice - Whether this is for spare devices modal
 * @returns {object} - Device option for modal dropdown
 */
export const convertDeviceToModalOption = (device, isSpareDevice = false) => {
  const oneTimePrice = parseFloat(device.oneTimePrice) || 0;
  const monthlyPrice = parseFloat(device.easyPaymentPrice) || 0;
  
  // Format prices to always show 2 decimal places
  const formatPrice = (price) => price.toFixed(2);
  
  // Format price display - for spare devices, only show upfront price
  let priceDisplay = '';
  if (isSpareDevice) {
    // For spare devices, only show upfront price
    priceDisplay = oneTimePrice > 0 ? `$${formatPrice(oneTimePrice)} upfront` : '';
  } else if (oneTimePrice > 0 && monthlyPrice > 0) {
    // For primary devices, show both upfront and monthly pricing
    priceDisplay = `$${formatPrice(oneTimePrice)} upfront / $${formatPrice(monthlyPrice)} monthly`;
  } else if (oneTimePrice > 0) {
    priceDisplay = `$${formatPrice(oneTimePrice)} upfront`;
  } else if (monthlyPrice > 0) {
    priceDisplay = `$${formatPrice(monthlyPrice)} monthly`;
  } else {
    priceDisplay = '';
  }

  return {
    fields: {
      value: device.name,
      label: `${device.name} (${device.deviceType})${priceDisplay ? ` - ${priceDisplay}` : ''}`,
      price: oneTimePrice,
      // For spare devices, set monthlyPayment to 0 to prevent monthly payment option from showing
      monthlyPayment: isSpareDevice ? 0 : monthlyPrice,
      deviceSku: device.deviceSku,
      deviceType: device.deviceType,
      familyType: device.familyType,
      hwcEligible: supportsHardwareCredit(device),
      // Store original device data for reference
      originalDevice: device
    }
  };
};

/**
 * Fetch devices from API and filter them
 * @param {string} modalType - 'addDevices' or 'addSpareDevices'
 * @param {object} orderData - Order data object
 * @param {object} formFields - Form fields object
 * @param {object} [contentfulValues] - { valuePaymentUpfront }
 * @returns {Promise<array>} - Promise resolving to filtered devices
 */
export const fetchAndFilterDevices = async (modalType, orderData, formFields, contentfulValues) => {
  try {
    // Import request dynamically to avoid circular dependencies
    const request = (await import('../../utils/request')).default;
    
    // Fetch all devices from API
    const response = await request.get('/orderDevices?limit=1000'); // Get all devices
    const devices = (response.data && response.data.data) || [];
    
    // Filter devices based on modal type
    let filteredDevices;
    if (modalType === 'addSpareDevices') {
      filteredDevices = filterDevicesForAddSpareDevices(devices, orderData, formFields);
    } else {
      filteredDevices = filterDevicesForAddDevices(devices, orderData, formFields, contentfulValues);
    }
    
    // Convert to modal option format
    return filteredDevices.map(device => convertDeviceToModalOption(device, modalType === 'addSpareDevices'));
    
  } catch (error) {
    return [];
  }
};
